import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Header from "../Header";
import moment from "moment";
import MessagePanel from "../components/MessagePanel";

export default function VocalCart() {
  const location = useLocation();
  const caseName = location.state?.caseName || "Voice Recording";

  const [currentScreen, setCurrentScreen] = useState("referral");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const [referralDate, setReferralDate] = useState({});
  const [transcriptionResult, setTranscriptionResult] = useState("");
  const [transcriptionResultEn, setTranscriptionResultEn] = useState("");
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionResultEn, setExtractionResultEn] = useState(null);
  const [displayLang, setDisplayLang] = useState("bn");
  const [reportResult, setReportResult] = useState(null);
  const [fieldExtractionResult, setFieldExtractionResult] = useState(null);
  const [messageConfig, setMessageConfig] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
    subMessage: "",
    callback: null,
  });
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startNew = async () => {
    setReferralDate({});
    setTranscriptionResult("");
    setTranscriptionResultEn("");
    setExtractionResult(null);
    setExtractionResultEn(null);
    setReportResult(null);
    setFieldExtractionResult(null);
    setDisplayLang("bn");
    audioChunksRef.current = [];
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    mediaRecorderRef.current = recorder;
    recorder.start(100);
  };

  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  const generateReferralID = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const toggleMic = () => {
    if (!isRecording) {
      startNew();
      setIsRecording(true);
      setSeconds(0);
    } else {
      setIsRecording(false);
      setSeconds(0);
      stopRecording();
    }
  };

  const isTranscriptionOnly = caseName === "Transcription";
  const isReportGeneration = caseName === "Report Generation";
  const isFieldExtraction = caseName === "Field Extraction";

  const saveRecord = async (audioFile, fields) => {
    try {
      const fd = new FormData();
      fd.append("audio", audioFile);
      fd.append("case_name", fields.case_name ?? "");
      fd.append("date_time", fields.date_time ?? "");
      fd.append("transcription", fields.transcription ?? "");
      fd.append("json_data", fields.json_data ?? "");
      fd.append("en_transcription", fields.en_transcription ?? "");
      fd.append("en_json", fields.en_json ?? "");
      await fetch(`${process.env.REACT_APP_PYTHON_API_URL}/save-record`, {
        method: "POST",
        body: fd,
      });
    } catch (err) {
      console.warn("save-record failed (non-blocking):", err);
    }
  };

  const stopRecording = async () => {
    setIsProcessing(true);
    try {
      const newId = generateReferralID();
      const entryDateTime = moment().utcOffset(6 * 60).format("YYYY-MM-DD HH:mm:ss");

      const blob = await new Promise((resolve) => {
        const recorder = mediaRecorderRef.current;
        recorder.onstop = () => {
          resolve(new Blob(audioChunksRef.current, { type: "audio/webm" }));
          recorder.stream.getTracks().forEach((t) => t.stop());
        };
        recorder.stop();
      });

      const file = new File([blob], `${newId}.webm`, {
        type: "audio/webm",
      });

      if (isTranscriptionOnly) {
        const formData = new FormData();
        formData.append("audio", file);
        formData.append("case_name", caseName);

        const response = await fetch(
          `${process.env.REACT_APP_PYTHON_API_URL}/transcription`,
          { method: "POST", body: formData }
        );
        const result = await response.json();

        if (result.status) {
          setReferralDate({ Id: newId, EntryDateTime: entryDateTime });
          setTranscriptionResult(result.spoken_text_bn ?? "");
          setTranscriptionResultEn(result.spoken_text_en ?? "");
          setExtractionResult(null);
          setExtractionResultEn(null);
          setReportResult(null);
          setFieldExtractionResult(null);
          setDisplayLang("bn");
          setIsProcessing(false);
          setCurrentScreen("details");
          saveRecord(file, {
            case_name: result.case_name ?? caseName,
            date_time: result.date_time ?? "",
            transcription: result.spoken_text_bn ?? "",
            json_data: "",
            en_transcription: result.spoken_text_en ?? "",
            en_json: "",
          });
        } else {
          setIsProcessing(false);
          setMessageConfig({
            show: true,
            type: "error",
            title: "Transcription Failed",
            message: result.message || "Transcription failed. Please try again.",
            subMessage: "",
          });
        }
        return;
      }

      if (isReportGeneration) {
        const transcribeForm = new FormData();
        transcribeForm.append("file", file);
        transcribeForm.append("language", "en");

        const transcribeResponse = await fetch(
          process.env.REACT_APP_REPORT_TRANSCRIBE_API_URL,
          { method: "POST", body: transcribeForm }
        );
        const transcribeData = await transcribeResponse.json();
        const transcriptText = transcribeData?.result?.text ?? "";

        const reportResponse = await fetch(
          process.env.REACT_APP_REPORT_GENERATE_API_URL,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              transcript: transcriptText,
              report_type: "gp_consultation",
            }),
          }
        );
        const reportData = await reportResponse.json();

        const reportDt = new Date().toLocaleString("en-US", {
          timeZone: "Asia/Dhaka",
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        setReferralDate({ Id: newId, EntryDateTime: entryDateTime });
        setTranscriptionResult(transcriptText);
        setTranscriptionResultEn("");
        setExtractionResult(null);
        setExtractionResultEn(null);
        setReportResult(reportData);
        setFieldExtractionResult(null);
        setDisplayLang("bn");
        setIsProcessing(false);
        setCurrentScreen("details");
        saveRecord(file, {
          case_name: caseName,
          date_time: reportDt,
          transcription: transcriptText,
          json_data: reportData ? JSON.stringify(reportData) : "",
          en_transcription: "",
          en_json: "",
        });
        return;
      }

      if (isFieldExtraction) {
        const transcribeForm = new FormData();
        transcribeForm.append("file", file);
        transcribeForm.append("language", "en");

        const transcribeResponse = await fetch(
          process.env.REACT_APP_REPORT_TRANSCRIBE_API_URL,
          { method: "POST", body: transcribeForm }
        );
        const transcribeData = await transcribeResponse.json();
        const transcriptText = transcribeData?.result?.text ?? "";

        const extractResponse = await fetch(
          process.env.REACT_APP_REPORT_EXTRACT_FIELDS_API_URL,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ transcript: transcriptText }),
          }
        );
        const extractData = await extractResponse.json();

        const extractDt = new Date().toLocaleString("en-US", {
          timeZone: "Asia/Dhaka",
          month: "long",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        setReferralDate({ Id: newId, EntryDateTime: entryDateTime });
        setTranscriptionResult(transcriptText);
        setTranscriptionResultEn("");
        setExtractionResult(null);
        setExtractionResultEn(null);
        setReportResult(null);
        setFieldExtractionResult(extractData);
        setDisplayLang("bn");
        setIsProcessing(false);
        setCurrentScreen("details");
        saveRecord(file, {
          case_name: caseName,
          date_time: extractDt,
          transcription: transcriptText,
          json_data: extractData ? JSON.stringify(extractData) : "",
          en_transcription: "",
          en_json: "",
        });
        return;
      }

      const formData = new FormData();
      formData.append("audio", file);
      formData.append("data", JSON.stringify({ Id: newId, EntryDateTime: entryDateTime }));
      formData.append("case_name", caseName);

      const audiouploadresponse = await fetch(
        `${process.env.REACT_APP_PYTHON_API_URL}/extract`,
        {
          method: "POST",
          body: formData,
        }
      );

      const audiouploadresult = await audiouploadresponse.json();

      if (audiouploadresult.status) {
        setReferralDate({
          Id: audiouploadresult.Id,
          EntryDateTime: audiouploadresult.EntryDateTime,
        });
        setTranscriptionResult(audiouploadresult.spoken_text ?? "");
        setTranscriptionResultEn(audiouploadresult.spoken_text_translated ?? "");
        setExtractionResult(audiouploadresult.extracted_info ?? null);
        setExtractionResultEn(audiouploadresult.extracted_info_translated ?? null);
        setDisplayLang("bn");
        setIsProcessing(false);
        setCurrentScreen("details");
        saveRecord(file, {
          case_name: audiouploadresult.case_name ?? caseName,
          date_time: audiouploadresult.date_time ?? "",
          transcription: audiouploadresult.spoken_text ?? "",
          json_data: audiouploadresult.extracted_info
            ? JSON.stringify(audiouploadresult.extracted_info)
            : "",
          en_transcription: audiouploadresult.spoken_text_translated ?? "",
          en_json: audiouploadresult.extracted_info_translated
            ? JSON.stringify(audiouploadresult.extracted_info_translated)
            : "",
        });
      } else {
        setIsProcessing(false);
        setMessageConfig({
          show: true,
          type: "error",
          title: "Upload Failed",
          message:
            audiouploadresult.message ||
            "Audio upload/extraction failed. Please try again.",
          subMessage: "",
        });
      }
    } catch (err) {
      setIsProcessing(false);
      setMessageConfig({
        show: true,
        type: "error",
        title: "Upload Failed",
        message: "Audio upload/extraction failed. Please try again.",
        subMessage: "",
      });
    }
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (totalSeconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  return (
    <div data-theme="dark">
      <div className="fixed inset-0 w-full h-full pointer-events-none z-0 transition-colors duration-500">
        <div
          className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full blur-[100px]"
          style={{ background: "var(--bg-grad-1)" }}
        ></div>
        <div
          className="absolute bottom-[-10%] right-[-10%] w-96 h-96 rounded-full blur-[100px]"
          style={{ background: "var(--bg-grad-2)" }}
        ></div>
      </div>

      <div
        id="app"
        className="relative z-10 w-full max-w-md mx-auto flex flex-col shadow-2xl transition-all duration-500 overflow-hidden"
        style={{
          height: "100dvh",
          background: "rgba(var(--bg-app), 0.8)",
          borderLeft: "1px solid var(--glass-border)",
          borderRight: "1px solid var(--glass-border)",
        }}
      >
        {/* Referral Screen */}
        {currentScreen === "referral" && (
          <div
            className="absolute inset-0 flex flex-col z-40"
            style={{ backgroundColor: "var(--bg-app)" }}
          >
            <Header BackTo={"/home"} />

            <div className="px-6 pt-2 pb-1 shrink-0 text-center">
              <h1 className="text-xl font-bold tracking-tight text-accent">
                {caseName}
              </h1>
            </div>

            {isProcessing && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                <div className="relative">
                  <div className="w-20 h-20 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
                  <div className="absolute inset-0 w-20 h-20 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" style={{ animationDirection: "reverse", animationDuration: "1.5s" }}></div>
                </div>
                <p className="mt-6 text-cyan-400 font-semibold text-lg animate-pulse">Processing Audio...</p>
                <p className="mt-2 text-muted text-sm">
                  {isTranscriptionOnly
                    ? "Transcribing audio"
                    : isReportGeneration
                    ? "Transcribing & generating report"
                    : isFieldExtraction
                    ? "Transcribing & extracting fields"
                    : "Extracting referral data"}
                </p>
              </div>
            )}

            <div className="flex-1 flex flex-col items-center justify-center relative z-10 w-full">
              {!isRecording && (
                <div className="text-muted text-sm tracking-widest uppercase animate-pulse">
                  Tap mic to start
                </div>
              )}

              {isRecording && (
                <div className="relative w-full h-80 flex items-center justify-center">
                  <div className="orb-container">
                    <div className="tech-ring-1"></div>
                    <div className="tech-ring-2"></div>
                    <div className="fluid-blob blob-2"></div>
                    <div className="fluid-blob blob-1"></div>
                    <div className="fluid-blob blob-3"></div>
                  </div>
                  <div className="absolute bottom-10 font-mono text-accent text-xs tracking-widest z-50 animate-pulse">
                    LISTENING...
                  </div>
                </div>
              )}
            </div>

            <div className="pb-12 flex flex-col items-center z-20 shrink-0">
              <div className="font-mono text-5xl font-light mb-8 text-muted">
                {formatTimer(seconds)}
              </div>
              <button
                onClick={toggleMic}
                className={`relative w-20 h-20 rounded-full shadow-xl flex items-center justify-center transition-all active:scale-90 hover:scale-105 cursor-pointer z-50 ${
                  isRecording ? "mic-ring-effect" : ""
                }`}
                style={{
                  backgroundColor: isRecording ? "#ef4444" : "var(--text-accent)",
                }}
              >
                {!isRecording ? (
                  <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                    <path d="M19 10v2a7 7 0 01-14 0v-2h-2v2a9 9 0 008 8.94V23h2v-2.06A9 9 0 0021 12v-2h-2z" />
                  </svg>
                ) : (
                  <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="7" y="7" width="10" height="10" rx="1" />
                  </svg>
                )}
              </button>

              <p className={`mt-4 text-xs uppercase tracking-widest ${isRecording ? "text-red-500" : "text-muted"}`}>
                {isRecording ? "STOP" : "Ready"}
              </p>
            </div>
          </div>
        )}

        {/* Details Screen */}
        {currentScreen === "details" && (
          <div
            className="absolute inset-0 flex flex-col z-50"
            style={{ backgroundColor: "var(--bg-app)" }}
          >
            <div className="p-6 pt-10 flex justify-between items-center shrink-0 glass z-20">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setCurrentScreen("referral")}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer hover:opacity-80 active:scale-90"
                  style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.3)", color: "#22d3ee" }}
                  title="Back to recording"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h2 className="text-xl font-bold text-accent">{caseName}</h2>
              </div>
              <select
                value={displayLang}
                onChange={(e) => setDisplayLang(e.target.value)}
                className="text-xs font-mono bg-cyan-500/20 text-cyan-400 px-3 py-1.5 rounded border border-cyan-500/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 hover:bg-cyan-500/30 hover:border-cyan-400/50 transition-colors cursor-pointer"
                aria-label="Select language"
              >
                <option value="bn" className="bg-slate-900 text-cyan-400">
                  Bn
                </option>
                <option value="en" className="bg-slate-900 text-cyan-400">
                  En
                </option>
              </select>
            </div>

            <div className="flex-1 overflow-y-auto p-6 pb-24 hide-scroll">
              <div className="space-y-5 animate-enter">

                {/* ID + Datetime */}
                <div className="flex flex-col gap-2">
                  <p className="text-center text-xs font-mono text-muted uppercase tracking-widest">
                    {referralDate.EntryDateTime
                      ? new Date(referralDate.EntryDateTime).toLocaleString("en-US", {
                          timeZone: "Asia/Dhaka",
                          weekday: "long",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : ""}
                  </p>
                  <div
                    className="w-full p-4 rounded-2xl border text-center flex flex-col items-center justify-center relative overflow-hidden"
                    style={{
                      backgroundColor: "var(--id-bg)",
                      borderColor: "var(--id-border)",
                    }}
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-50"></div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-muted mb-1">
                      Generated ID
                    </p>
                    <h1 className="text-4xl font-mono font-bold tracking-widest text-accent drop-shadow-sm">
                      {referralDate.Id || ""}
                    </h1>
                  </div>
                </div>

                {/* Transcription */}
                {((displayLang === "bn" && transcriptionResult) || (displayLang === "en" && transcriptionResultEn)) && (
                  <div className="flex flex-col gap-2">
                    <label className="input-label">Transcription</label>
                    <div
                      className="w-full p-4 rounded-2xl border overflow-hidden"
                      style={{
                        backgroundColor: "var(--id-bg)",
                        borderColor: "var(--id-border)",
                        minHeight: "2.5rem",
                      }}
                    >
                      <pre
                        className="m-0 text-sm whitespace-pre-wrap break-words font-sans text-left"
                        style={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                      >
                        {displayLang === "bn" ? transcriptionResult : transcriptionResultEn}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Report JSON */}
                {isReportGeneration && reportResult && (
                  <div className="flex flex-col gap-2">
                    <label className="input-label">Report JSON</label>
                    <div
                      className="w-full p-4 rounded-2xl border overflow-hidden"
                      style={{
                        backgroundColor: "var(--id-bg)",
                        borderColor: "var(--id-border)",
                        minHeight: "2.5rem",
                      }}
                    >
                      <pre
                        className="m-0 text-xs whitespace-pre-wrap break-words font-mono text-left"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          color: "var(--text-main)",
                        }}
                      >
                        {JSON.stringify(reportResult, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Field Extraction — structured cards */}
                {isFieldExtraction && fieldExtractionResult && (() => {
                  const FIELD_META = {
                    chief_complaint:     { label: "Chief Complaint",      icon: "🩺", color: "#22d3ee" },
                    assessment:          { label: "Assessment",           icon: "📋", color: "#a78bfa" },
                    primary_diagnosis:   { label: "Primary Diagnosis",    icon: "🔬", color: "#f472b6" },
                    secondary_diagnosis: { label: "Secondary Diagnosis",  icon: "🔍", color: "#fb923c" },
                    vital_signs:         { label: "Vital Signs",          icon: "💓", color: "#34d399" },
                    medications:         { label: "Medications",          icon: "💊", color: "#60a5fa" },
                    laboratory_tests:    { label: "Laboratory Tests",     icon: "🧪", color: "#facc15" },
                    allergies:           { label: "Allergies",            icon: "⚠️", color: "#f87171" },
                    chronic_conditions:  { label: "Chronic Conditions",   icon: "🏥", color: "#c084fc" },
                    risk_factors:        { label: "Risk Factors",         icon: "⚡", color: "#fb923c" },
                    notes:               { label: "Notes",                icon: "📝", color: "#94a3b8" },
                  };

                  const renderFieldValue = (key, value, color) => {
                    if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) return null;

                    if (Array.isArray(value)) {
                      return (
                        <div className="flex flex-col gap-1.5 mt-1">
                          {value.map((item, i) => (
                            <div
                              key={i}
                              className="flex items-start gap-2 rounded-xl px-3 py-2"
                              style={{ background: `${color}12`, border: `1px solid ${color}25` }}
                            >
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5"
                                style={{ background: `${color}25`, color }}
                              >
                                {i + 1}
                              </div>
                              <span className="text-sm leading-snug" style={{ color: "var(--text-main)" }}>
                                {typeof item === "object" ? JSON.stringify(item) : String(item)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    if (typeof value === "object") {
                      return (
                        <div className="flex flex-col gap-1.5 mt-1">
                          {Object.entries(value).filter(([, v]) => v !== null && v !== "").map(([k, v]) => (
                            <div key={k} className="flex items-center gap-2">
                              <span
                                className="text-[10px] uppercase tracking-wider font-semibold min-w-[90px]"
                                style={{ color: `${color}cc` }}
                              >
                                {k.replace(/_/g, " ")}
                              </span>
                              <span
                                className="text-sm font-semibold"
                                style={{ color: "var(--text-main)" }}
                              >
                                {String(v)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    return (
                      <p className="text-sm mt-1 leading-relaxed" style={{ color: "var(--text-main)" }}>
                        {String(value)}
                      </p>
                    );
                  };

                  const entries = Object.entries(fieldExtractionResult).filter(([, v]) =>
                    v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)
                  );

                  return (
                    <div className="flex flex-col gap-3">
                      <label className="input-label">Extracted Fields</label>
                      {entries.map(([key, value]) => {
                        const meta = FIELD_META[key] || { label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), icon: "📌", color: "#94a3b8" };
                        const c = meta.color;
                        return (
                          <div
                            key={key}
                            className="relative rounded-2xl px-4 py-3 overflow-hidden"
                            style={{
                              background: `linear-gradient(135deg, ${c}12 0%, ${c}06 100%)`,
                              border: `1px solid ${c}30`,
                            }}
                          >
                            <div
                              className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
                              style={{ background: `linear-gradient(180deg, ${c}, ${c}60)` }}
                            />
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base">{meta.icon}</span>
                              <span
                                className="text-[10px] uppercase tracking-widest font-bold"
                                style={{ color: c }}
                              >
                                {meta.label}
                              </span>
                            </div>
                            {renderFieldValue(key, value, c)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Extracted Data */}
                {!isTranscriptionOnly && !isReportGeneration && !isFieldExtraction && (extractionResult || extractionResultEn) && (() => {
                  const ORDER_CASES = {
                    "Grocery Order":           { icon: "🛒", color: "#22d3ee",  label: "Order Items" },
                    "Automotive Dealer Order": { icon: "🔧", color: "#a78bfa",  label: "Parts Ordered" },
                    "Pharma Order":            { icon: "💊", color: "#34d399",  label: "Medicines" },
                  };
                  const meta = ORDER_CASES[caseName];
                  const activeExtraction = displayLang === "bn" ? extractionResult : extractionResultEn;
                  const isOrderCase = !!meta && activeExtraction && Array.isArray(activeExtraction.items) && activeExtraction.items.length > 0;

                  if (isOrderCase) {
                    const items = activeExtraction.items;
                    const nameKey = items[0]?.medicine_name !== undefined ? "medicine_name" : "product_name";
                    const c = meta.color;

                    return (
                      <div className="flex flex-col gap-3">
                        {/* Header row */}
                        <div className="flex items-center justify-between">
                          <label className="input-label">{meta.label}</label>
                          <span
                            className="text-[11px] font-bold px-3 py-0.5 rounded-full"
                            style={{ color: c, background: `${c}20`, border: `1px solid ${c}40` }}
                          >
                            {items.length} {items.length === 1 ? "item" : "items"}
                          </span>
                        </div>

                        {/* Item rows — full-width horizontal cards */}
                        <div className="flex flex-col gap-2">
                          {items.map((item, i) => {
                            const name = item[nameKey] || item.product_name || item.medicine_name || "—";
                            const qty  = item.quantity || "";
                            const unit = item.unit || "";
                            const qtyDisplay = unit ? `${qty} ${unit}`.trim() : qty;
                            return (
                              <div
                                key={i}
                                className="relative flex items-center gap-3 rounded-2xl px-4 py-3 overflow-hidden"
                                style={{
                                  background: `linear-gradient(135deg, ${c}12 0%, ${c}06 100%)`,
                                  border: `1px solid ${c}30`,
                                }}
                              >
                                {/* left accent bar */}
                                <div
                                  className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl"
                                  style={{ background: `linear-gradient(180deg, ${c}, ${c}60)` }}
                                />

                                {/* index bubble */}
                                <div
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
                                  style={{ background: `${c}25`, color: c }}
                                >
                                  {i + 1}
                                </div>

                                {/* icon */}
                                <span className="text-xl shrink-0">{meta.icon}</span>

                                {/* name */}
                                <p
                                  className="flex-1 text-sm font-semibold leading-snug"
                                  style={{ color: "var(--text-main)" }}
                                >
                                  {name}
                                </p>

                                {/* quantity badge */}
                                {(qty || unit) && (
                                  <div
                                    className="shrink-0 flex flex-col items-center justify-center rounded-xl px-3 py-1.5 min-w-[52px] text-center"
                                    style={{ background: `${c}20`, border: `1px solid ${c}40` }}
                                  >
                                    <span className="text-[9px] uppercase tracking-widest text-muted leading-none mb-0.5">qty</span>
                                    <span className="text-sm font-bold leading-none" style={{ color: c }}>{qtyDisplay || "—"}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Summary footer */}
                        <div
                          className="flex items-center justify-center gap-2 rounded-xl py-2.5"
                          style={{ background: `${c}10`, border: `1px dashed ${c}30` }}
                        >
                          <span className="text-[11px] text-muted uppercase tracking-widest">Total</span>
                          <span className="font-bold text-sm" style={{ color: c }}>{items.length} {items.length === 1 ? "product" : "products"}</span>
                        </div>
                      </div>
                    );
                  }

                  /* ── fallback for non-order cases ── */
                  const fallbackExtraction = displayLang === "bn" ? extractionResult : extractionResultEn;
                  return (
                    <div className="flex flex-col gap-2">
                      <label className="input-label">Extracted Data</label>
                      <div
                        className="w-full rounded-2xl border overflow-hidden"
                        style={{ backgroundColor: "var(--id-bg)", borderColor: "var(--id-border)" }}
                      >
                        {Object.entries(fallbackExtraction || {}).map(([key, value]) => {
                          const displayKey = key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                          const isEmpty = value === "" || value === null || (Array.isArray(value) && value.length === 0);
                          if (isEmpty) return null;
                          return (
                            <div
                              key={key}
                              className="flex flex-col gap-1 px-4 py-3 border-b last:border-b-0"
                              style={{ borderColor: "var(--id-border)" }}
                            >
                              <span className="text-[10px] uppercase tracking-widest text-muted font-semibold">{displayKey}</span>
                              {Array.isArray(value) ? (
                                <ul className="list-disc list-inside space-y-0.5">
                                  {value.map((item, i) => (
                                    <li key={i} className="text-sm" style={{ color: "var(--text-main)" }}>
                                      {typeof item === "object" ? JSON.stringify(item, null, 0) : String(item)}
                                    </li>
                                  ))}
                                </ul>
                              ) : typeof value === "object" ? (
                                <div className="space-y-1">
                                  {Object.entries(value).map(([k, v]) =>
                                    v ? (
                                      <div key={k} className="flex gap-2 text-sm">
                                        <span className="text-muted capitalize min-w-[90px]">{k.replace(/_/g, " ")}:</span>
                                        <span style={{ color: "var(--text-main)" }}>{String(v)}</span>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm" style={{ color: "var(--text-main)" }}>{String(value)}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-4 glass flex gap-4 z-20">
              <button
                onClick={() => setCurrentScreen("referral")}
                className="flex-1 py-3 rounded-xl border border-cyan-500/30 text-cyan-400 font-semibold hover:bg-cyan-500/10 transition-colors"
              >
                Record Again
              </button>
            </div>
          </div>
        )}

        <MessagePanel
          show={messageConfig.show}
          type={messageConfig.type}
          title={messageConfig.title}
          message={messageConfig.message}
          subMessage={messageConfig.subMessage}
          onClose={() => setMessageConfig({ ...messageConfig, show: false })}
          onCallback={messageConfig.callback}
          autoClose={true}
          duration={3000}
        />
      </div>
    </div>
  );
}
